// =======================
// src/contexto.js
// =======================
import React, { createContext, useState, useEffect, useRef } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";

import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";

import { api } from "@api/api";

const TEMPO_MAXIMO_INATIVIDADE = 60 * 60 * 1000;
const INTERVALO_RENOVACAO = 45 * 60 * 1000;
const INTERVALO_VERIFICACAO = 60 * 1000;
const INTERVALO_NOVA_TENTATIVA = 60 * 1000;

export const CondicaoContext = createContext();

export const CondicaoProvider = ({ children }) => {
  const [condicao, setCondicao] = useState(true);
  const [role, setRole] = useState(null);
  const [userId, setUserId] = useState(null);
  const [firstAccess, setFirstAccess] = useState(false);
  const [token, setToken] = useState(null);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [sessaoExpirada, setSessaoExpirada] = useState(false);
  const [loading, setLoading] = useState(true);

  const ultimaAtividadeRef = useRef(Date.now());
  const ultimaRenovacaoRef = useRef(0);
  const ultimaTentativaRenovacaoRef = useRef(0);
  const renovacaoEmAndamentoRef = useRef(false);
  const encerramentoEmAndamentoRef = useRef(false);

  const [mantenedoraRegistrada, setMantenedoraRegistrada] = useState(null);

  const verificarMantenedora = async () => {
    try {
      const response = await fetch(api.verify(), {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Falha ao verificar mantenedora");
      }

      const data = await response.json();

      setMantenedoraRegistrada(data.isRegistered === true);
    } catch (error) {
      console.error("Erro ao verificar mantenedora:", error);
    }
  };

  const updateAuthState = (
    newRole,
    newToken = null,
    newFirstAccess = false,
  ) => {
    setLoading(true);
    setMantenedoraRegistrada(null);
    setRole(newRole);
    setToken(newToken);
    setFirstAccess(newRole === "superintendent" ? newFirstAccess : false);
    setCondicao(newRole === null);
    setLoginSuccess(true);
    setTimeout(() => setLoginSuccess(false), 3000);
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(api.auth(), {
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error("Falha ao buscar usuário");
        }

        const data = await res.json();

        // Verifica a mantenedora antes de liberar o menu
        if (data.role === "user") {
          await verificarMantenedora();
        } else {
          setMantenedoraRegistrada(null);
        }

        setRole(data.role || null);
        setUserId(data.userId ?? null);
        setFirstAccess(
          data.role === "superintendent" ? data.firstAccess || false : false,
        );
        setCondicao(!data.role);
      } catch (err) {
        console.error("Erro ao buscar usuário:", err);
        setMantenedoraRegistrada(null);
        setRole(null);
        setUserId(null);
        setFirstAccess(false);
        setCondicao(true);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [loginSuccess]);

  useEffect(() => {
    if (!role) {
      return undefined;
    }

    let efeitoAtivo = true;

    let temporizadorInatividadeId;

    ultimaAtividadeRef.current = Date.now();

    ultimaRenovacaoRef.current = 0;

    ultimaTentativaRenovacaoRef.current = 0;

    renovacaoEmAndamentoRef.current = false;

    encerramentoEmAndamentoRef.current = false;

    const encerrarSessao = () => {
      if (encerramentoEmAndamentoRef.current) {
        return;
      }

      encerramentoEmAndamentoRef.current = true;

      fetch(api.logout(), {
        method: "POST",

        credentials: "include",
      }).catch((error) => {
        console.error("Erro ao encerrar sessão:", error);
      });

      setMantenedoraRegistrada(null);

      setCondicao(true);

      setRole(null);

      setUserId(null);

      setToken(null);

      setLoginSuccess(false);

      setFirstAccess(false);

      setSessaoExpirada(true);
    };

    const renovarSessao = async () => {
      if (
        renovacaoEmAndamentoRef.current ||
        encerramentoEmAndamentoRef.current
      ) {
        return;
      }

      const agora = Date.now();

      if (
        agora - ultimaTentativaRenovacaoRef.current <
        INTERVALO_NOVA_TENTATIVA
      ) {
        return;
      }

      ultimaTentativaRenovacaoRef.current = agora;

      renovacaoEmAndamentoRef.current = true;

      try {
        const response = await fetch(api.renewSession(), {
          method: "POST",

          credentials: "include",
        });

        if (!efeitoAtivo) {
          return;
        }

        if (response.status === 401 || response.status === 403) {
          encerrarSessao();

          return;
        }

        if (!response.ok) {
          console.error(`Falha ao renovar sessão. Status: ${response.status}`);

          return;
        }

        ultimaRenovacaoRef.current = Date.now();
      } catch (error) {
        if (efeitoAtivo) {
          console.error("Erro ao renovar sessão:", error);
        }
      } finally {
        renovacaoEmAndamentoRef.current = false;
      }
    };

    const agendarEncerramento = () => {
      window.clearTimeout(temporizadorInatividadeId);

      temporizadorInatividadeId = window.setTimeout(() => {
        encerrarSessao();
      }, TEMPO_MAXIMO_INATIVIDADE);
    };

    const registrarAtividade = () => {
      if (encerramentoEmAndamentoRef.current) {
        return;
      }

      const agora = Date.now();

      if (agora - ultimaAtividadeRef.current >= TEMPO_MAXIMO_INATIVIDADE) {
        encerrarSessao();

        return;
      }

      ultimaAtividadeRef.current = agora;

      agendarEncerramento();

      if (agora - ultimaRenovacaoRef.current >= INTERVALO_RENOVACAO) {
        renovarSessao();
      }
    };

    const verificarInatividade = () => {
      if (Date.now() - ultimaAtividadeRef.current >= TEMPO_MAXIMO_INATIVIDADE) {
        encerrarSessao();
      }
    };

    const eventos = ["mousedown", "keydown", "scroll", "touchstart", "input"];

    const opcoesEventos = {
      capture: true,

      passive: true,
    };

    eventos.forEach((evento) => {
      window.addEventListener(
        evento,

        registrarAtividade,

        opcoesEventos,
      );
    });

    const intervaloVerificacaoId = window.setInterval(
      verificarInatividade,

      INTERVALO_VERIFICACAO,
    );

    agendarEncerramento();

    renovarSessao();

    return () => {
      efeitoAtivo = false;

      window.clearTimeout(temporizadorInatividadeId);

      window.clearInterval(intervaloVerificacaoId);

      eventos.forEach((evento) => {
        window.removeEventListener(
          evento,

          registrarAtividade,

          opcoesEventos,
        );
      });
    };
  }, [role]);

  return (
    <CondicaoContext.Provider
      value={{
        mantenedoraRegistrada,
        setMantenedoraRegistrada,
        condicao,
        setCondicao,
        role,
        setRole,
        userId,
        firstAccess,
        setFirstAccess,
        token,
        setToken,
        loginSuccess,
        setLoginSuccess,
        logout: () => {
          setMantenedoraRegistrada(null);
          setCondicao(true);
          setRole(null);
          setUserId(null);
          setToken(null);
          setLoginSuccess(false);
          setFirstAccess(false);
        },
        updateAuthState,
        loading,
      }}
    >
      {children}

  {/* ← MODAL DE SESSÃO EXPIRADA */}
  <Dialog
    open={sessaoExpirada}
    onClose={() => {}}
    disableEscapeKeyDown
    fullWidth
    maxWidth="xs"
    aria-labelledby="titulo-sessao-expirada"
    aria-describedby="descricao-sessao-expirada"
    sx={{
      '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(15, 23, 42, 0.58)',
        backdropFilter: 'blur(2px)',
      },
    }}
    PaperProps={{
      sx: {
        borderRadius: '18px',
        overflow: 'hidden',
        maxWidth: '430px',
        margin: 2,
      },
    }}
  >
    <Box
      sx={{
        height: '7px',
        background:
          'linear-gradient(90deg, #1565c0 0%, #42a5f5 100%)',
      }}
    />

    <DialogContent
      sx={{
        textAlign: 'center',
        px: { xs: 3, sm: 4 },
        pt: 4,
        pb: 2,
      }}
    >
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          backgroundColor: '#eaf3ff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 22px',
        }}
      >
        <AccessTimeRoundedIcon
          sx={{
            fontSize: 52,
            color: '#1976d2',
          }}
        />
      </Box>

      <Typography
        id="titulo-sessao-expirada"
        component="h2"
        variant="h5"
        sx={{
          color: '#123a6d',
          fontWeight: 700,
          mb: 1.5,
        }}
      >
        Sessão expirada
      </Typography>

      <Typography
        id="descricao-sessao-expirada"
        sx={{
          color: '#5f6b7a',
          fontSize: '1rem',
          lineHeight: 1.6,
        }}
      >
        Você ficou 1 hora sem interagir com o sistema. Para continuar
        com segurança, faça login novamente.
      </Typography>
    </DialogContent>

    <DialogActions
      sx={{
        px: { xs: 3, sm: 4 },
        pt: 1,
        pb: 4,
      }}
    >
      <Button
        fullWidth
        variant="contained"
        onClick={() => setSessaoExpirada(false)}
        sx={{
          py: 1.3,
          borderRadius: '10px',
          fontWeight: 700,
          fontSize: '1rem',
          textTransform: 'none',
          background:
            'linear-gradient(90deg, #1565c0 0%, #2196f3 100%)',
        }}
      >
        Fazer login novamente
      </Button>
    </DialogActions>
  </Dialog>
</CondicaoContext.Provider>
  );
};
